import { t } from '@/lib/i18n';
import type { Department } from '../schemas';
import styles from './DepartmentPicker.module.css';

interface DepartmentPickerProps {
  departments: Department[];
  /** Ordered department ids the user has picked, or null = "Not sure yet" */
  value: string[] | null;
  onChange: (next: string[] | null) => void;
  maxPicks?: number;
}

export function DepartmentPicker({
  departments,
  value,
  onChange,
  maxPicks = 3,
}: DepartmentPickerProps) {
  const picks = value ?? [];

  function toggleDept(id: string) {
    if (value === null) {
      onChange([id]);
      return;
    }
    if (picks.includes(id)) {
      const next = picks.filter((p) => p !== id);
      onChange(next.length === 0 ? null : next);
      return;
    }
    if (picks.length >= maxPicks) return;
    onChange([...picks, id]);
  }

  return (
    <div className={styles.root}>
      <p className={styles.label}>{t('What kind of work do you need help with?')}</p>
      <p className={styles.hint}>
        {t('Pick up to {n}. The order tells us where to focus first.', { n: String(maxPicks) })}
      </p>
      <div className={styles.chips}>
        {departments.map((d) => {
          const idx = picks.indexOf(d.id);
          const selected = idx >= 0;
          return (
            <button
              key={d.id}
              type="button"
              className={selected ? styles.chipSelected : styles.chip}
              onClick={() => toggleDept(d.id)}
              aria-pressed={selected}
            >
              {selected ? `${idx + 1}. ${d.name}` : d.name}
            </button>
          );
        })}
        <button
          type="button"
          className={value === null ? styles.chipSelected : styles.chip}
          onClick={() => onChange(null)}
          aria-pressed={value === null}
        >
          {t('Not sure yet')}
        </button>
      </div>
    </div>
  );
}
